import { fd, tt, joinCampaign } from '../../../../shared';
import {
  CampaignsGetResponse_Campaign,
  CLandingpageColorKey,
  CLandingpageMediaKey,
} from '../../../../apis/api-client';
import { t } from 'i18next';

type CampaignCardProps = {
  campaign: CampaignsGetResponse_Campaign;
  elevated?: boolean;
};

function getHeaderImage(campaign: CampaignsGetResponse_Campaign): string | undefined {
  const media = campaign.landingpage?.media ?? [];
  return media.find(m => m.key === CLandingpageMediaKey.HEADER)?.url ?? undefined;
}

function getColors(campaign: CampaignsGetResponse_Campaign): { brand: string; accent: string } {
  const colors = campaign.landingpage?.colors ?? [];
  const brand = colors.find(c => c.key === CLandingpageColorKey.BRAND_COLOR)?.hexCode;
  const accent = colors.find(c => c.key === CLandingpageColorKey.ACCENT_COLOR)?.hexCode;
  return {
    brand: brand ? `#${brand}` : '#0e2150',
    accent: accent ? `#${accent}` : '#46f2b6',
  };
}

function renderDate(campaign: CampaignsGetResponse_Campaign): string {
  if (!campaign.event?.happeningFrom) return '';
  const from = fd(campaign.event.happeningFrom, false);
  if (!campaign.event.happeningTo || campaign.event.happeningFrom === campaign.event.happeningTo) {
    return from;
  }
  return `${from} – ${fd(campaign.event.happeningTo, false)}`;
}

export default function CampaignCard({ campaign, elevated = false }: CampaignCardProps) {
  const { brand, accent } = getColors(campaign);
  const headerUrl = getHeaderImage(campaign);
  const logoUrl = campaign.event?.logo ?? undefined;
  const primaryGenre = campaign.event?.genres?.[0];
  const monogram = (campaign.event?.title ?? '?').charAt(0).toUpperCase();
  const eventTitle = tt(campaign.event?.id ?? '', 'event_title', campaign.event?.title ?? '');
  const location = campaign.event?.location
    ? tt(campaign.event.id!, 'event_location', campaign.event.location)
    : undefined;
  const dateStr = renderDate(campaign);

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    if (campaign.program?.isPublished && campaign.program?.id) {
      joinCampaign(campaign.program.id);
    }
  }

  return (
    <a
      className={elevated ? 'ev-card ev-card--elevated' : 'ev-card'}
      href="#"
      onClick={handleClick}
      style={{ '--b': brand, '--a': accent } as React.CSSProperties}
    >
      <div className="ev-card__top">
        {headerUrl && <img src={headerUrl} alt="" className="ev-card__img" />}
        <div className="ev-card__scrim" />
        {elevated && <span className="ev-card__featured">{t('joinPage.v2.directory.featured')}</span>}
        {primaryGenre && (
          <span className="ev-card__genre">
            {tt(primaryGenre.id!, 'genre_title', primaryGenre.title!)}
          </span>
        )}
      </div>
      {/* Logo lives on .ev-card so it isn't clipped by __top's overflow:hidden */}
      <span className="ev-card__logo" aria-hidden="true">
        {logoUrl ? <img src={logoUrl} alt={eventTitle} /> : monogram}
      </span>
      <div className="ev-card__body">
        <div className="ev-card__name">{eventTitle}</div>
        <div className="ev-card__meta">
          {[location, dateStr].filter(Boolean).map((part, i, arr) => (
            <span key={i}>
              {part}
              {i < arr.length - 1 && <span className="sep" />}
            </span>
          ))}
        </div>
        <div className="ev-card__foot">
          <span className="ev-card__open">
            {campaign.program?.isPublished
              ? t('joinPage.v2.directory.openProgram')
              : t('landingPage.header.comingSoon')}
            {campaign.program?.isPublished && <span className="arr">→</span>}
          </span>
        </div>
      </div>
    </a>
  );
}
